import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Typography} from '@mui/material';

import {Dispatch, Store} from 'spectra-logic-ui';
import {fetchResource} from 'spectra-logic-ui/actions';
import {ExpandableTable} from 'spectra-logic-ui/components';

import ButtonToolbar from '@/components/button_toolbar';
import DeleteDialog from '@/components/delete_dialog';
import {IAMGroup, IAMPolicy} from '@/iam/types';
import PutPolicyDialog from '@/iam/groups/details/form/put';

type Props = {
  group: IAMGroup;
  policies?: IAMPolicy[];
  error?: boolean;
  fetching?: boolean;
  fetchPolicies?: () => Promise<any>;
}

const Policies = ({group, policies=[], error, fetching, fetchPolicies}: Props) => {
  const [selectedPolicyName, setSelectedPolicyName] = useState('');

  useEffect(() => {
    if (fetchPolicies) {
      fetchPolicies();
    }
  }, [group]);

  useEffect(() => {
    // Most like the user selected a different IAM group. If the new list of policies
    // contains a policy with the same name as the one that's already selected (from the old group),
    // keep it selected. This could be useful when switching back and forth between groups to compare
    // their policies.
    if (selectedPolicyName &&
      policies.find((policy) => policy.name === selectedPolicyName) === undefined) {
      setSelectedPolicyName('');
    }
  }, [policies]);

  const deleteResource = `iam/groups/${group.accountid}/${group.name}/policies`;

  const selectedPolicy = policies.find((policy) => policy.name === selectedPolicyName);

  return (
    <>
      <ButtonToolbar>
        <ButtonToolbar.CreateDialogButton
          title='Create'
          dialog={(props: any) => <PutPolicyDialog group={group} policies={policies} {...props} />}
          disabled={error || fetching}
        />
        <ButtonToolbar.EditDialogButton
          title='Edit'
          dialog={(props: any) => (
            <PutPolicyDialog group={group} policy={selectedPolicy} {...props} />
          )}
          disabled={!selectedPolicyName}
        />
        <ButtonToolbar.DeleteDialogButton
          dialog={(props: any) => (
            <DeleteDialog
              id={selectedPolicyName} name={selectedPolicyName}
              resource={deleteResource} title='Delete IAM Group Policy' {...props}
            />
          )}
          disabled={!selectedPolicyName}
        />
      </ButtonToolbar>
      <ExpandableTable>
        <ExpandableTable.Body isLoading={fetching} hasError={error} emptyMessage='IAM group contains no policies'>
          {policies.map((policy) => (
            <ExpandableTable.BodyRow
              key={policy.name} selected={policy.name === selectedPolicyName}
              onClick={() => setSelectedPolicyName(policy.name === selectedPolicyName ? '' : policy.name)}
              details={<Document document={policy.document} />}
            >
              <ExpandableTable.Cell>{policy.name}</ExpandableTable.Cell>
            </ExpandableTable.BodyRow>
          ))}
        </ExpandableTable.Body>
      </ExpandableTable>
    </>
  );
};

const Document = ({document}: {document?: object}) => {
  if (!document) {
    return <Typography>No document for policy</Typography>;
  }
  return (
    <pre>{JSON.stringify(document, null, 2)}</pre>
  );
};

const mapStateToProps = (state: Store, {group}: Props) => {
  const path = `iam/groups/${group.accountid}/${group.name}/policies`;
  const policiesState = state.resources[path] || {};
  return {
    error: policiesState.error,
    fetching: policiesState.fetching,
    policies: policiesState.data,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, {group}: Props) => {
  const path = `iam/groups/${group.accountid}/${group.name}/policies`;
  return {
    fetchPolicies: () => dispatch(fetchResource(path)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Policies);
